<template>
    <div class="number-filter">
        <div class="holder">
            <FilterTypeSelector
                :options="filterOptions"
                :default-filter="defaultFilterVal"
                @input="onFilterTypeChange"
            />
            <div class="filter-container">
                <div v-if="!isRange(filterType)" class="not-range-container">
                    <div
                        v-tooltip="{
                            content: filterText,
                            placement: 'bottom',
                            distance: 5,
                            delay: { show: 500, hide: 100 },
                        }"
                        style="width: 100%"
                    >
                        <AonInput
                            v-model="filterValue"
                            :placeholder="filterText"
                            search-input
                            :error="inputError"
                            :data-cy="`filter.number-input`"
                            @clear="onClear"
                            @update:modelValue="onFilter"
                        />
                    </div>
                </div>
                <div v-if="isRange(filterType)" class="range-container">
                    <div
                        v-tooltip="{
                            disabled: filterValues[0] !== null,
                            content: filterValues[0],
                            distance: 5,
                            delay: { show: 500, hide: 100 },
                        }"
                        style="width: 100%"
                    >
                        <AonInput
                            v-model="filterValues[0]"
                            class="range-input-from"
                            placeholder="From"
                            :error="inputErrorRangeOne"
                            :data-cy="`filter.range-input-from`"
                            @update:modelValue="onRangeFilterFrom"
                        />
                    </div>
                    <div
                        v-tooltip="{
                            disabled: filterValues[1] !== null,
                            content: filterValues[1],
                            distance: 5,
                            delay: { show: 500, hide: 100 },
                        }"
                        style="width: 100%"
                    >
                        <AonInput
                            v-model="filterValues[1]"
                            class="range-input-to"
                            placeholder="To"
                            :error="inputErrorRangeTwo"
                            :data-cy="`filter.range-input-to`"
                            @update:modelValue="onRangeFilterTo"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { debounce } from 'lodash-es';
import FilterTypeSelector from './FilterTypeSelector.vue';
import AonInput from '@moatmetrics/armory/src/components/base/forms/AonInput.vue';
import numbro from 'numbro';

export default {
    name: 'Numberfilter',
    components: {
        FilterTypeSelector,
        AonInput,
    },
    props: {
        columnName: {
            type: String,
            default: '',
        },
        columnLabel: {
            type: String,
            default: '',
        },
        defaultFilter: {
            type: [String, Object],
            default: () => {
                return null;
            },
        },
        filters: {
            type: Array,
            default: function () {
                return [];
            },
        },
        filterState: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            filterType: null,
            filterValue: null,
            filterValues: [null, null],
            filterValuesActual: [null, null],
            filterValueActual: null,
            filterTypeSelected: null,
            filterText: 'Number Filter',
            allFilters: [
                { itemText: 'Equals', itemValue: 'equals' },
                { itemText: 'Greater than', itemValue: 'greater_than' },
                { itemText: 'Less than', itemValue: 'less_than' },
                { itemText: 'Range', itemValue: 'range' },
                { itemText: 'Starts with', itemValue: 'starts_with' },
            ],
            inputError: false,
            inputErrorRangeOne: false,
            inputErrorRangeTwo: false,
            rangeError: false,
        };
    },
    computed: {
        detail() {
            return {
                filterColumn: this.columnName,
                filterValue: this.filterValue,
                filterValueActual: this.filterValueActual,
                filterType: this.filterType,
                filterLabel: this.columnLabel,
                filter: 'number',
            };
        },
        defaultFilterVal() {
            return this.filterTypeSelected
                ? this.filterTypeSelected
                : this.defaultFilter
                ? this.defaultFilter
                : this.filterOptions[0];
        },
        filterOptions() {
            return this.filters && this.filters.length > 0
                ? this.filters
                : this.allFilters;
        },
    },
    watch: {
        filterState: {
            handler() {
                this.setupFilters();
            },
            deep: true,
        },
    },
    beforeMount() {
        this.setupFilters();
    },
    methods: {
        setupFilters() {
            if (!this.filterState) {
                this.clearFilter();

                return;
            }
            this.setFilter();
        },
        clearFilter() {
            this.resetValues();
            this.setFilterTypeInfo();
        },
        setFilterTypeInfo(val = null) {
            const filterVal = val ? val : this.defaultFilterVal;
            if (!filterVal) return;
            if (typeof filterVal === 'string') {
                this.filterTypeSelected = {
                    itemText: filterVal,
                    itemValue: filterVal.toLowerCase().replace(' ', '_'),
                };
            } else {
                this.filterTypeSelected = filterVal;
            }
            this.filterType = this.filterTypeSelected.itemText;
        },
        onRangeFilterFrom: debounce(function (val) {
            this.doRangeFilterChecks(0, val);
        }, 300),
        onRangeFilterTo: debounce(function (val) {
            this.doRangeFilterChecks(1, val);
        }, 300),
        doRangeFilterChecks(index, val) {
            this.filterValues[index] = val;
            const numeralVal = numbro(val?.toLowerCase())._value;
            if (numeralVal !== undefined || val === '') {
                index === 0
                    ? (this.inputErrorRangeOne = false)
                    : (this.inputErrorRangeTwo = false);
                this.filterValuesActual[index] = numeralVal || val;
                this.checkErrors();
                this.onRangeFilter();
            } else {
                index === 0
                    ? (this.inputErrorRangeOne = true)
                    : (this.inputErrorRangeTwo = true);
                this.checkErrors();
            }
        },
        onFilter: debounce(function (val) {
            const numeralVal = numbro(val?.toLowerCase())._value;
            if (numeralVal !== undefined || val === '') {
                this.inputError = false;
                this.filterValueActual = numeralVal || val;
                this.checkErrors();
                this.$emit('onFilter', this.detail);
            } else {
                this.inputError = true;
                this.checkErrors();
            }
        }, 300),
        onRangeFilter() {
            if (this.rangeError) return;
            this.filterValue =
                this.filterValues[0] + '|' + this.filterValues[1];
            this.filterValueActual =
                this.filterValuesActual[0] + '|' + this.filterValuesActual[1];

            this.$emit('onFilter', this.detail);
        },
        onClear() {
            this.resetValues();
            this.resetErrors();
            this.$emit('onFilter', this.detail);
        },
        resetValues() {
            this.filterValue = null;
            this.filterValues = [null, null];
            this.filterValueActual = null;
            this.filterValuesActual = [null, null];
        },
        resetErrors() {
            this.inputError = false;
            this.inputErrorRangeOne = false;
            this.inputErrorRangeTwo = false;
            this.$emit('input-error', false);
        },
        onFilterTypeChange(value) {
            this.resetValues();
            this.resetErrors();

            this.filterType = value.itemText;
            this.filterTypeSelected = value;
        },
        setFilter() {
            this.filterValue = null;
            this.filterValueActual = null;
            this.setFilterTypeInfo();

            this.setFilterTypeInfo(this.filterState.filterType);
            if (this.filterState.filterType === 'Range') {
                this.filterValues = this.filterState.filterValue.split('|');
            }
            this.filterValue = this.filterState.filterValue;
            this.filterValueActual = this.filterState.filterValueActual;
        },
        isRange(val) {
            return val?.toLowerCase() === 'range';
        },
        checkErrors() {
            const badValues = [null, ''];
            if (
                (badValues.includes(this.filterValuesActual[0]) &&
                    !badValues.includes(this.filterValuesActual[1])) ||
                (badValues.includes(this.filterValuesActual[1]) &&
                    !badValues.includes(this.filterValuesActual[0]))
            ) {
                this.$emit('input-error', true);
                this.rangeError = true;
            } else if (
                !this.inputError &&
                !this.inputErrorRangeOne &&
                !this.inputErrorRangeTwo
            ) {
                this.$emit('input-error', false);
                this.rangeError = false;
            } else {
                this.$emit('input-error', true);
                this.rangeError = true;
            }
        },
    },
};
</script>

<style lang="scss">
.number-filter {
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: flex-end;
    flex-wrap: wrap;

    .bold {
        width: 100%;
    }

    .holder {
        width: 100%;
        display: flex;
        justify-content: flex-start;
        align-items: flex-end;
    }

    .filter-container {
        width: 100%;
        display: flex;
        justify-content: left;
        align-items: center;

        .range-container {
            width: 100%;
            display: flex;
            justify-content: left;
            align-items: center;

            .range-input-from {
                input {
                    border-radius: 4px 0 0 4px;
                    border-right: none;
                }
            }

            .range-input-to {
                input {
                    border-radius: 0 4px 4px 0;
                }
            }
        }

        .not-range-container {
            width: 100%;
        }
    }
}
</style>
